/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment } from 'react'

import type { EuiComboBoxOptionOption } from '@elastic/eui'
import { EuiComboBox, EuiFormRow } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import type { ReactNode } from 'react'

export type AllRoleSelectorProps<RoleId extends string> = {
  organizationId: string
  options: Array<EuiComboBoxOptionOption<RoleId>>
  customRolesEnabled: boolean
  singleSelection: boolean
  singleSelectionAsCount: number
  selectedOptions: Array<EuiComboBoxOptionOption<RoleId>>
  resourceTypeDisplay: ReactNode
  'data-test-id': string
  noRolesPlaceholder: (singleSelection: boolean) => string
  onChange: (selectedOptions: Array<EuiComboBoxOptionOption<RoleId>>) => void
}

const AllRoleSelector = <RoleId extends string>({
  options,
  singleSelection,
  singleSelectionAsCount,
  'data-test-id': dataTestId,
  noRolesPlaceholder,
  selectedOptions,
  resourceTypeDisplay,
  onChange,
}: AllRoleSelectorProps<RoleId>): JSX.Element => (
  <Fragment>
    <FormattedMessage
      id='roles-assignments.project.helper'
      defaultMessage='Set {count, plural, one {a role} other {one or more roles}} for individual or all {resourceType}.'
      values={{
        resourceType: resourceTypeDisplay,
        count: singleSelectionAsCount,
      }}
    />
    <EuiFormRow
      label={
        <FormattedMessage
          id='roles-assignments.project.all.description'
          defaultMessage='{count, plural, one {Role} other {Roles}} for all {resourceType}'
          values={{
            resourceType: resourceTypeDisplay,
            count: singleSelectionAsCount,
          }}
        />
      }
      fullWidth={true}
    >
      <EuiComboBox<RoleId>
        data-test-subj='allRoleSelector'
        data-test-id={dataTestId}
        placeholder={noRolesPlaceholder(singleSelection)}
        singleSelection={singleSelection}
        options={options}
        selectedOptions={selectedOptions}
        onChange={onChange}
      />
    </EuiFormRow>
  </Fragment>
)

export default AllRoleSelector
